// @ts-nocheck
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from './icons';

/**
 * DropdownMenu — controlled-uncontrolled hybrid menu anchored to a trigger.
 *
 *  - Pass `trigger` (any element); a click toggles the menu.
 *  - Children are `<MenuItem>` / `<MenuSection>` / `<MenuDivider>`.
 *  - Closes on outside click, ESC, and item click (unless item has `keepOpen`).
 *
 * The menu renders in a portal with fixed positioning so it floats above the
 * page rather than being clipped by ancestors with `overflow` (e.g. the
 * `overflow-x-auto` scroll container that wraps every Table).
 *
 * Direction is decided automatically: it opens downward unless there isn't
 * enough room below the trigger and there's more room above. Pass
 * `direction="up"`/`"down"` to force a side.
 */
const DropdownMenu = ({ trigger, align = 'left', direction, width, children, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const ref = useRef(null);
  const menuRef = useRef(null);

  const place = () => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const gap = 6;
    const menuH = menuRef.current ? menuRef.current.offsetHeight : 0;
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // Auto-flip: open up only when below can't fit the menu and above has more room.
    const openUp = direction === 'up'
      || (direction !== 'down' && menuH > spaceBelow - gap && spaceAbove > spaceBelow);
    setCoords({
      top: openUp ? null : r.bottom + gap,
      bottom: openUp ? window.innerHeight - r.top + gap : null,
      left: align === 'right' ? null : r.left,
      right: align === 'right' ? window.innerWidth - r.right : null,
    });
  };

  // The menu is mounted (hidden) whenever open, so menuRef is available to
  // measure here in a single pass before we reveal it.
  useLayoutEffect(() => { if (open) place(); }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onReflow = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open]);

  return (
    <div ref={ref} className={['relative inline-block', className].join(' ')}>
      <span onClick={() => setOpen((o) => !o)}>{trigger}</span>
      {open && createPortal((
        <div
          ref={menuRef}
          role="menu"
          className="fixed z-50"
          style={{
            top: coords?.top ?? undefined,
            bottom: coords?.bottom ?? undefined,
            left: coords?.left ?? undefined,
            right: coords?.right ?? undefined,
            visibility: coords ? 'visible' : 'hidden',
            minWidth: width || 220,
            background: 'var(--mov-paper)',
            border: '1px solid var(--mov-line-strong)',
            borderRadius: 12,
            padding: 6,
            boxShadow: 'var(--mov-shadow-float)',
          }}
        >
          {React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) return child;
            return React.cloneElement(child, {
              onSelect: () => {
                if (child.props.onSelect) child.props.onSelect();
                if (!child.props.keepOpen) setOpen(false);
              },
            });
          })}
        </div>
      ), document.body)}
    </div>
  );
};

export const MenuTrigger = ({ children, icon: Icon, className = '' }) => (
  <button
    type="button"
    className={[
      'inline-flex items-center gap-2 cursor-pointer text-[13px]',
      className,
    ].join(' ')}
    style={{
      background: 'var(--mov-paper)',
      border: '1px solid var(--mov-line-strong)',
      borderRadius: 9,
      padding: '8px 12px',
      color: 'var(--mov-ink)',
    }}
  >
    {Icon && <Icon size={13} />}
    {children}
    <ChevronDownIcon size={12} className="text-mov-ink-mute" />
  </button>
);

export const MenuSection = ({ children }) => (
  <div
    className="mov-mono"
    style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--mov-ink-mute)', padding: '8px 10px 4px' }}
  >
    {children}
  </div>
);

export const MenuDivider = () => (
  <div style={{ height: 1, background: 'var(--mov-line)', margin: '6px 4px' }} />
);

export const MenuItem = ({ icon: Icon, right, danger, onSelect, children, disabled, keepOpen, className = '' }) => (
  <button
    type="button"
    role="menuitem"
    onClick={disabled ? undefined : onSelect}
    disabled={disabled}
    className={['flex items-center gap-2.5 w-full text-left transition-colors', danger ? 'text-mov-danger' : '', className].join(' ')}
    style={{
      padding: '8px 10px',
      borderRadius: 7,
      fontSize: 13,
      background: 'transparent',
      border: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      color: danger ? 'var(--mov-danger)' : 'var(--mov-ink)',
    }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = danger ? '#fbe7e9' : 'var(--mov-paper-2)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
  >
    {Icon && <Icon size={14} className="shrink-0 text-mov-ink-mute" />}
    <span className="flex-1">{children}</span>
    {right && <span className="mov-mono text-[10.5px] text-mov-ink-mute">{right}</span>}
  </button>
);

// Avoid unused-param lint by exporting keepOpen consumer
MenuItem.keepOpenProp = 'keepOpen';

export default DropdownMenu;
